import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  MonetizationOn as MonetizationOnIcon,
  Notifications as NotificationsIcon,
  Password as PasswordIcon,
  Person as PersonIcon,
  Phonelink as PhonelinkIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import type { MenuItem } from 'src/components/common';
import { FullscreenLoader, ItemDetailsLayout } from 'src/components/common';
import { useCurrentUserPeo } from 'src/hooks/peo';
import { useCurrentUser, useUserAvatar } from 'src/hooks/user';
import { peoReceivesOwnRevenueShare } from 'src/utils/revenueShare';
import { getUserFullName } from 'src/utils/user';

export const AccountLayoutPEOAdmin: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { id: userId, user } = useCurrentUser();
  const { userAvatar, userAvatarLoading, updateUserAvatar, userAvatarUpdating } =
    useUserAvatar(userId);

  const { peo, loading } = useCurrentUserPeo();

  const fullName = getUserFullName(user);
  const title = user?.title;

  const menuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: appRoutes.account.personalInformation,
        title: 'Personal Information',
        icon: PersonIcon,
      },
      {
        link: appRoutes.account.companyInformation,
        title: 'Company information',
        icon: AccountBalanceIcon,
      },
      {
        link: appRoutes.account.wallet,
        title: 'Wallet',
        icon: MonetizationOnIcon,
        hide: !peo?.revenueShareSetting || !peoReceivesOwnRevenueShare(peo),
      },
      {
        link: appRoutes.account.settings,
        title: 'Settings',
        icon: SettingsIcon,
      },
      {
        link: appRoutes.account.notifications,
        title: 'Notifications',
        icon: NotificationsIcon,
        divider: true,
      },
      {
        link: appRoutes.account.security,
        title: 'Sign-in & Security',
        icon: PasswordIcon,
      },
      {
        link: appRoutes.account.setupMfa,
        title: 'Multi-factor Authentication',
        icon: PhonelinkIcon,
      },
    ],
    [peo],
  );

  if (loading) {
    return (
      <FullscreenLoader
        sx={{
          height: '500px',
        }}
      />
    );
  }

  return (
    <ItemDetailsLayout
      name={fullName}
      subtitle={title}
      logoUrl={userAvatar?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updateUserAvatar,
        loading: userAvatarLoading || userAvatarUpdating,
      }}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};
